<script setup lang="ts">
import PageHeader from '@/components/page-header.vue'
import PageContent from '@/components/page-content.vue'
import ContentLoader from "@/components/content-loader.vue";
import { faDownload, faCircleInfo, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import ButtonSecondary from '@/components/button-secondary.vue';
import ButtonHighlighted from '@/components/button-highlighted.vue';
import InputSelect from "@/components/input-select.vue";
import { filterQueries, mainQueries } from '@/features/tariffs/queries';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch, reactive } from 'vue';
import Tabs from '@/components/tabs-teal.vue'
import splashImage from '@/assets/tariffs/dashboard/splash.svg'
import HeroSplash from '@/components/hero-splash.vue'
import { Season, ChargeName, PSO } from '@/features/tariffs/constants'
import type { Tariffs } from '@/features/tariffs/api/fetchTariffs';
import { faHandPointUp } from '@fortawesome/pro-solid-svg-icons';
import ModalTouProfile from './modal-tou-profie.vue';
import { useApproveTariffs } from '@/features/tariffs/mutations';
import { useModalState } from '@/shared/composables/useModalState';
import useNotifications from '@/composables/notifications';
import { useDebounce } from '@vueuse/core';
import DataTablePagination from '@/components/data-table-pagination.vue'
import VerticallyCentered from "@/components/vertically-centered.vue";
import { useAuthenticatedUser } from '@/shared/composables/useAuthenticatedUser'
import {
  usePageQueryParameter,
  usePageSizeQueryParameter,
} from '@/composables/query-parameters'
import type { ApproveResponse } from '@/features/tariffs/api/approveTariffs';

interface PeriodValue {
  rate: number | null
  oldRate: number | null
  changePercent: number | null
  isActive: boolean
  bandId: number
  label?: string
}

interface TariffRow {
  tariffId: number
  groupKey: string | null
  providerId: number
  providerName: string
  tariffName: string
  source: 'AMMP' | 'UNIFII'
  transZone: string | null
  voltage: string | null
  isTimeOfUse: boolean
  isApproved: boolean
  yearOfTariff: number

  blocks: { label: string }[]
  blocksW: PeriodValue[]
  blocksS: PeriodValue[]

  peakW: PeriodValue[]
  standardW: PeriodValue[]
  offPeakW: PeriodValue[]
  peakS: PeriodValue[]
  standardS: PeriodValue[]
  offPeakS: PeriodValue[]

  basicChargeW: PeriodValue[]
  demandChargeW: PeriodValue[]
  accessChargeW: PeriodValue[]
  serviceChargeW: PeriodValue[]
  capacityChargeW: PeriodValue[]
  reactiveChargeW: PeriodValue[]
  legacyChargeW: PeriodValue[]
  networkDemandChargeW: PeriodValue[]
  generationChargeW: PeriodValue[]
  electrificationChargeW: PeriodValue[]
  affordabilityChargeW: PeriodValue[]
  ancillaryChargeW: PeriodValue[]
  creditReverseChargeW: PeriodValue[]
  fixedMonthlyChargeW: PeriodValue[]
  fixedDailyChargeW: PeriodValue[]

  basicChargeS: PeriodValue[]
  demandChargeS: PeriodValue[]
  accessChargeS: PeriodValue[]
  serviceChargeS: PeriodValue[]
  capacityChargeS: PeriodValue[]
  reactiveChargeS: PeriodValue[]
  legacyChargeS: PeriodValue[]
  networkDemandChargeS: PeriodValue[]
  generationChargeS: PeriodValue[]
  electrificationChargeS: PeriodValue[]
  affordabilityChargeS: PeriodValue[]
  ancillaryChargeS: PeriodValue[]
  creditReverseChargeS: PeriodValue[]
  fixedMonthlyChargeS: PeriodValue[]
  fixedDailyChargeS: PeriodValue[]
}

function makePeriod(period: any, label?: string): PeriodValue {
  return {
    rate: period.RateInCents,
    oldRate: period.OldRateInCents ?? null,
    changePercent: period.RateChangePercent ?? null,
    isActive: period.IsActive,
    bandId: period.Id,
    label,
  }
}

function mapTariffs(tariffs: Tariffs | undefined, isCompare: boolean): TariffRow[] {
  if (!Array.isArray(tariffs)) return []

  return tariffs.map(tariff => {
    const row: TariffRow = {
      tariffId: isCompare ? (Number(tariff.ExternalId?.split(',')[0]) || 0) : tariff.Id,
      groupKey: tariff.GroupKey ?? null,
      providerId: tariff.UtilityProviderId,
      providerName: tariff.UtilityProviderName,
      tariffName: tariff.Name,
      source: tariff.ExternalId ? 'AMMP' : 'UNIFII',
      transZone: tariff.TransZone,
      voltage: tariff.Voltage,
      isTimeOfUse: tariff.IsTimeOfUse,
      isApproved: tariff.IsApproved,
      yearOfTariff: 0,

      blocks: [], blocksW: [], blocksS: [],
      peakW: [], standardW: [], offPeakW: [],
      peakS: [], standardS: [], offPeakS: [],

      basicChargeW: [], demandChargeW: [], accessChargeW: [], serviceChargeW: [],
      capacityChargeW: [], reactiveChargeW: [], legacyChargeW: [],
      networkDemandChargeW: [], generationChargeW: [], electrificationChargeW: [],
      affordabilityChargeW: [], ancillaryChargeW: [], creditReverseChargeW: [],
      fixedMonthlyChargeW: [], fixedDailyChargeW: [],

      basicChargeS: [], demandChargeS: [], accessChargeS: [], serviceChargeS: [],
      capacityChargeS: [], reactiveChargeS: [], legacyChargeS: [],
      networkDemandChargeS: [], generationChargeS: [], electrificationChargeS: [],
      affordabilityChargeS: [], ancillaryChargeS: [], creditReverseChargeS: [],
      fixedMonthlyChargeS: [], fixedDailyChargeS: [],
    }

    for (const period of tariff.Periods) {
      row.yearOfTariff = period.YearOfTariff
      const isWinter = period.Season === Season.Winter
      const isSummer = period.Season === Season.Summer
      const isNone   = period.Season === Season.None

      // ── Flat-rate energy blocks ──────────────────────────────────────────────
      if (period.ChargeName === ChargeName.Energy && !tariff.IsTimeOfUse) {
        const blockLabel = (period.StartOfPeriod || period.EndOfPeriod)
          ? `${period.StartOfPeriod} - ${period.EndOfPeriod}`
          : ''
        const band = makePeriod(period, blockLabel)

        if (isSummer) {
          row.blocksS.push(band)
          if (band.rate && !row.blocks.some(b => b.label === blockLabel))
            row.blocks.push({ label: blockLabel })
          continue
        }
        if (isWinter) {
          row.blocksW.push(band)
          if (band.rate && !row.blocks.some(b => b.label === blockLabel))
            row.blocks.push({ label: blockLabel })
          continue
        }
        // isNone — push to both but only add label once
        if (isNone) {
          const bandW = makePeriod(period, blockLabel)
          const bandS = makePeriod(period, blockLabel)
          row.blocksW.push(bandW)
          row.blocksS.push(bandS)
          if (band.rate && !row.blocks.some(b => b.label === blockLabel))
            row.blocks.push({ label: blockLabel })
          continue
        }
      }

      const band = makePeriod(period)

      // ── Fixed charges ────────────────────────────────────────────────────────
      const chargeMap: Record<string, keyof TariffRow> = {
        [ChargeName.Basic]:           isWinter || isNone ? 'basicChargeW'          : 'basicChargeS',
        [ChargeName.Demand]:          isWinter || isNone ? 'demandChargeW'         : 'demandChargeS',
        [ChargeName.Access]:          isWinter || isNone ? 'accessChargeW'         : 'accessChargeS',
        [ChargeName.Service]:         isWinter || isNone ? 'serviceChargeW'        : 'serviceChargeS',
        [ChargeName.Capacity]:        isWinter || isNone ? 'capacityChargeW'       : 'capacityChargeS',
        [ChargeName.Reactive]:        isWinter || isNone ? 'reactiveChargeW'       : 'reactiveChargeS',
        [ChargeName.Legacy]:          isWinter || isNone ? 'legacyChargeW'         : 'legacyChargeS',
        [ChargeName.NetworkDemand]:   isWinter || isNone ? 'networkDemandChargeW'  : 'networkDemandChargeS',
        [ChargeName.Generation]:      isWinter || isNone ? 'generationChargeW'     : 'generationChargeS',
        [ChargeName.Electrification]: isWinter || isNone ? 'electrificationChargeW': 'electrificationChargeS',
        [ChargeName.Affordability]:   isWinter || isNone ? 'affordabilityChargeW'  : 'affordabilityChargeS',
        [ChargeName.Ancillary]:       isWinter || isNone ? 'ancillaryChargeW'      : 'ancillaryChargeS',
        [ChargeName.CreditRev]:       isWinter || isNone ? 'creditReverseChargeW'  : 'creditReverseChargeS',
        [ChargeName.FixedDaily]:      isWinter || isNone ? 'fixedDailyChargeW'     : 'fixedDailyChargeS',
        [ChargeName.FixedMonthly]:    isWinter || isNone ? 'fixedMonthlyChargeW'   : 'fixedMonthlyChargeS',
      }

      const targetKey = chargeMap[period.ChargeName]
      if (targetKey) {
        ;(row[targetKey] as PeriodValue[]).push(band)
        continue
      }

      // ── TOU energy — only push to the season it belongs to ──────────────────
      if (tariff.IsTimeOfUse) {
        // isNone means it applies to both seasons — push to both explicitly
        const pushW = isWinter || isNone
        const pushS = isSummer || isNone

        if (pushW) {
          if (period.TimeOfUse === PSO.Peak)     row.peakW.push(band)
          if (period.TimeOfUse === PSO.Standard) row.standardW.push(band)
          if (period.TimeOfUse === PSO.OffPeak)  row.offPeakW.push(band)
        }
        if (pushS) {
          if (period.TimeOfUse === PSO.Peak)     row.peakS.push(band)
          if (period.TimeOfUse === PSO.Standard) row.standardS.push(band)
          if (period.TimeOfUse === PSO.OffPeak)  row.offPeakS.push(band)
        }
      }
    }

    return row
  })
}

// ─── Selection ────────────────────────────────────────────────────────────────

const selectedRecords = ref<string[]>([])
const hasSelection = computed(() => selectedRecords.value.length > 0)

function isSelected(groupKey: string | null) {
  return groupKey ? selectedRecords.value.includes(groupKey) : false
}

function toggleSelection(groupKey: string | null) {
  if (!groupKey) return
  const i = selectedRecords.value.indexOf(groupKey)
  if (i === -1) selectedRecords.value.push(groupKey)
  else          selectedRecords.value.splice(i, 1)
}

function toggleAll(event: MouseEvent) {
  selectedRecords.value = (event.target as HTMLInputElement).checked
    ? tariffRows.value.map(r => r.groupKey).filter(Boolean) as string[]
    : []
}

// ─── Filter state ─────────────────────────────────────────────────────────────

const page     = usePageQueryParameter()
const pageSize = usePageSizeQueryParameter()
const regions            = ref<number[]>([])
const utilityProviders   = ref<number[]>([])
const tariffIds          = ref<number[]>([])
const compareProviders   = ref<string[]>([])
const compareTariffNames = ref<string[]>([])

const { addSuccess, addError } = useNotifications()
const { data: regionData }          = useQuery(filterQueries.fetchRegion())
const { data: utilityProviderData } = useQuery(filterQueries.fetchProviders(regions))
const { data: tariffNamesData }     = useQuery(filterQueries.fetchTariffNames(utilityProviders))
const { data: yearsData }           = useQuery(filterQueries.fetchTariffYears(tariffIds))
const { data: authenticatedUser, isLoading: isLoadingAuthenticatedUser } = useAuthenticatedUser()
const years        = computed(() => ['Compare & Approve', ...(yearsData.value?.map(String) ?? [])])
const selectedYear = ref('')
const touProfiles  = reactive(useModalState())

watch(years, (val) => {
  if (val.length > 0) {
    selectedYear.value = val.length > 1 ? val[1] : val[0]
    page.value = 1
  }
}, { immediate: true })

watch(regions,          () => { utilityProviders.value = []; tariffIds.value = [] }, { immediate: true })
watch(utilityProviders, () => { tariffIds.value = [] },                              { immediate: true })
watch(selectedYear,     () => { selectedRecords.value = []; page.value = 1 },        { immediate: true })

const debouncedIds = useDebounce(tariffIds, 1000)
const isCompare    = computed(() => selectedYear.value.startsWith('Compare'))

const { data: tariffsData, isFetching } = useQuery(
  mainQueries.fetchTariffs({ 
    ids: debouncedIds, 
    yearOfTariff: selectedYear, 
    page, 
    pageSize,
    filter: '',
    sorting: [],
  })
)

const { mutateAsync, isPending } = useApproveTariffs(
  computed(() => authenticatedUser.value?.id)
)
const tariffs = computed(() => tariffsData.value?.data ?? [])
const meta    = computed(() => tariffsData.value?.meta)

// ─── Mapped rows ──────────────────────────────────────────────────────────────

const tariffRows = computed<TariffRow[]>(() => {
  const rows = mapTariffs(tariffs.value, isCompare.value)
  if (!compareProviders.value.length && !compareTariffNames.value.length) return rows
  return rows.filter(r =>
    (!compareProviders.value.length   || compareProviders.value.includes(r.providerName)) &&
    (!compareTariffNames.value.length || compareTariffNames.value.includes(r.tariffName))
  )
})

const compareProviderOptions = computed(() => [...new Map(tariffs.value.map(r => [r.UtilityProviderName, { Name: r.UtilityProviderName }])).values()])
const compareTariffOptions   = computed(() => [...new Map(tariffs.value.map(r => [r.Name, { Name: r.Name }])).values()])

// ─── CSV ──────────────────────────────────────────────────────────────────────

function bandsToCsv(bands: PeriodValue[], prefix: string, isCompare: boolean) {
  return bands.map((b, i) => ({
    [`${prefix}_rate_${i}`]: b.rate,
    ...(isCompare ? { [`${prefix}_old_${i}`]: b.oldRate, [`${prefix}_pct_${i}`]: b.changePercent } : {}),
  })).reduce((a, b) => ({ ...a, ...b }), {})
}

function downloadCsv() {
  const records = hasSelection.value
    ? tariffRows.value.filter(r => selectedRecords.value.includes(r.groupKey ?? ''))
    : tariffRows.value

  if (!records.length) return

  const flatForCsv = records.map(r => ({
    TariffId:          r.tariffId,
    Provider:          r.providerName,
    Tariff:            r.tariffName,
    Source:            r.source,
    TransZone:         r.transZone,
    Voltage:           r.voltage,
    Year:              r.yearOfTariff,
    BlocksWinter:      r.blocks.map(b => b.label).join('|'),
    BlocksWinterRates: r.blocksW.map(b => b.rate).join('|'),
    BlocksSummer:      r.blocks.map(b => b.label).join('|'),
    BlocksSummerRates: r.blocksS.map(b => b.rate).join('|'),
    ...bandsToCsv(r.peakW,     'PeakW',     isCompare.value),
    ...bandsToCsv(r.standardW, 'StandardW', isCompare.value),
    ...bandsToCsv(r.offPeakW,  'OffPeakW',  isCompare.value),
    ...bandsToCsv(r.peakS,     'PeakS',     isCompare.value),
    ...bandsToCsv(r.standardS, 'StandardS', isCompare.value),
    ...bandsToCsv(r.offPeakS,  'OffPeakS',  isCompare.value),
    ...bandsToCsv(r.basicChargeW,           'BasicChargeW',           isCompare.value),
    ...bandsToCsv(r.demandChargeW,          'DemandChargeW',          isCompare.value),
    ...bandsToCsv(r.accessChargeW,          'AccessChargeW',          isCompare.value),
    ...bandsToCsv(r.serviceChargeW,         'ServiceChargeW',         isCompare.value),
    ...bandsToCsv(r.capacityChargeW,        'CapacityChargeW',        isCompare.value),
    ...bandsToCsv(r.reactiveChargeW,        'ReactiveChargeW',        isCompare.value),
    ...bandsToCsv(r.legacyChargeW,          'LegacyChargeW',          isCompare.value),
    ...bandsToCsv(r.networkDemandChargeW,   'NetworkDemandChargeW',   isCompare.value),
    ...bandsToCsv(r.generationChargeW,      'GenerationChargeW',      isCompare.value),
    ...bandsToCsv(r.electrificationChargeW, 'ElectrificationChargeW', isCompare.value),
    ...bandsToCsv(r.affordabilityChargeW,   'AffordabilityChargeW',   isCompare.value),
    ...bandsToCsv(r.ancillaryChargeW,       'AncillaryChargeW',       isCompare.value),
    ...bandsToCsv(r.creditReverseChargeW,   'CreditReverseChargeW',   isCompare.value),
    ...bandsToCsv(r.basicChargeS,           'BasicChargeS',           isCompare.value),
    ...bandsToCsv(r.demandChargeS,          'DemandChargeS',          isCompare.value),
    ...bandsToCsv(r.accessChargeS,          'AccessChargeS',          isCompare.value),
    ...bandsToCsv(r.serviceChargeS,         'ServiceChargeS',         isCompare.value),
    ...bandsToCsv(r.capacityChargeS,        'CapacityChargeS',        isCompare.value),
    ...bandsToCsv(r.reactiveChargeS,        'ReactiveChargeS',        isCompare.value),
    ...bandsToCsv(r.legacyChargeS,          'LegacyChargeS',          isCompare.value),
    ...bandsToCsv(r.networkDemandChargeS,   'NetworkDemandChargeS',   isCompare.value),
    ...bandsToCsv(r.generationChargeS,      'GenerationChargeS',      isCompare.value),
    ...bandsToCsv(r.electrificationChargeS, 'ElectrificationChargeS', isCompare.value),
    ...bandsToCsv(r.affordabilityChargeS,   'AffordabilityChargeS',   isCompare.value),
    ...bandsToCsv(r.ancillaryChargeS,       'AncillaryChargeS',       isCompare.value),
    ...bandsToCsv(r.creditReverseChargeS,   'CreditReverseChargeS',   isCompare.value),
  }))

  const headers = Object.keys(flatForCsv[0])
  const csv = [
    headers.join(','),
    ...flatForCsv.map(r => headers.map(h => JSON.stringify(r[h as keyof typeof r] ?? '')).join(','))
  ].join('\r\n')

  const filename = (tariffNamesData.value ?? [])
    .filter(t => tariffIds.value.includes(t.Id))
    .map(t => t.Name).join('-')
    .replace(/[^a-z0-9-_]/gi, '_').slice(0, 100) + '-' + Date.now() + '.csv'

  const link = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
    download: filename,
    style: 'visibility:hidden',
  })
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// ─── Approve ──────────────────────────────────────────────────────────────────

async function approveTariffs() {
  try {
    const res = await mutateAsync(selectedRecords.value)
    selectedRecords.value = []

    if ((res?.Data?.Rejected?.length ?? 0) > 0) {
      addError({
        title: `${res?.Data?.Approved ?? 0} approved, ${res?.Data?.Rejected?.length ?? 0} could not be approved.`,
        message: res?.Data?.Rejected?.join('\n') ?? ''
      })
    } else {
      addSuccess({ title: `${res?.Data?.Approved ?? 0} tariffs approved successfully.` })
    }
  } catch (error: any) {
  const data = error?.data as ApproveResponse | null
  if (data?.Data?.Rejected?.length) {
    addError({
      title: 'No tariffs approved — newer versions already exist.',
      message: data.Data.Rejected.join('\n')
    })
  } else {
    addError({ title: 'Approval failed.' })
  }
}
}
</script>

<template>
  <PageHeader>
    <div>
      <div class="d-inline-block text-nowrap">
        <label class="me-2">Region</label>
        <InputSelect name="region" :items="regionData ?? []" multiple item-value="Id" item-text="Name"
          v-model:selected="regions" :display-limit="1" inline />
      </div>
      <div class="d-inline-block text-nowrap ms-4">
        <label class="me-2">Utility Provider</label>
        <InputSelect name="utilityProvider" :items="utilityProviderData ?? []" multiple item-value="Id" item-text="Name"
          v-model:selected="utilityProviders" :display-limit="1" inline />
      </div>
      <div class="d-inline-block text-nowrap ms-4">
        <label class="me-2">Tariff</label>
        <InputSelect :searchable="true" name="tariff" :items="tariffNamesData ?? []" multiple item-value="Id"
          item-text="Name" v-model:selected="tariffIds" :display-limit="1" inline />
      </div>
    </div>
    <div>
      <ButtonSecondary :icon="faPenToSquare" @click="touProfiles.open()">TOU Profile</ButtonSecondary>
    </div>
  </PageHeader>

  <PageContent>
    <VerticallyCentered v-if="years.length == 1 && tariffRows.length === 0 && !isFetching && !isLoadingAuthenticatedUser">
      <HeroSplash title="Customize Your View!"
        sub-title="Select options from the dropdowns to populate the screen." :image-url="splashImage" />
    </VerticallyCentered>

    <Tabs v-else :tabs="years" v-model="selectedYear" />

    <PageContent class="d-flex flex-column h-100">
      <ContentLoader :loading="isFetching || isPending || isLoadingAuthenticatedUser" :variant="tariffRows.length === 0 ? 'overlay' : 'table'">

        <div v-if="isCompare && tariffRows.length > 0" class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-2 ms-4">
            <FontAwesomeIcon :icon="faCircleInfo" class="fa-2x text-yellow" />
            <span>These filters only apply to the Compare &amp; Approve tab:</span>
          </div>
          <div class="d-inline-block text-nowrap ms-4">
            <label class="me-2">Utility Provider</label>
            <InputSelect name="compareProvider" :items="compareProviderOptions" multiple item-value="Name"
              item-text="Name" v-model:selected="compareProviders" :display-limit="1" inline />
          </div>
          <div class="d-inline-block text-nowrap ms-4">
            <label class="me-2">Tariff</label>
            <InputSelect :searchable="true" name="compareTariff" :items="compareTariffOptions" multiple item-value="Name"
              item-text="Name" v-model:selected="compareTariffNames" :display-limit="1" inline />
          </div>
        </div>

        <div class="table-responsive" v-if="tariffRows.length > 0">
          <table class="table w-100 table-hover u-data-table table-sm">
            <thead>
              <tr class="no-bottom-border">
                <th colspan="7" class="border-right"></th>
                <th :colspan="isCompare ? 3 : 2" class="text-center border-right">Block High</th>
                <th :colspan="isCompare ? 2 : 2" class="text-center border-right">Block Low</th>
                <th :colspan="isCompare ? 6 : 3" class="text-center border-right">High Demand</th>
                <th :colspan="isCompare ? 6 : 3" class="text-center border-right">Low Demand</th>
                <th :colspan="isCompare ? 30 : 15" class="text-center border-right">High Demand Charges</th>
                <th :colspan="isCompare ? 30 : 15" class="text-center border-right">Low Demand Charges</th>
              </tr>
              <tr class="text-nowrap">
                <th>
                  <input type="checkbox" class="form-check-input" ref="$selectAll" @click="toggleAll"
                    v-tooltip="selectedRecords.length > 0 ? `${selectedRecords.length} selected` : undefined" />
                </th>
                <th>Utility Provider</th>
                <th>Tariff</th>
                <th>Year</th>
                <th>Source</th>
                <th>Trans Zone</th>
                <th class="border-right">Voltage</th>

                <!-- Block Winter -->
                <th>Block</th>
                <th v-if="isCompare">Rate<br>Old</th>
                <th class="border-right">Rate<span v-if="isCompare"><br>New</span></th>

                <!-- Block Summer -->
                <th v-if="isCompare">Rate<br>Old</th>
                <th class="border-right">Rate<span v-if="isCompare"><br>New</span></th>

                <!-- TOU Winter -->
                <template v-for="col in ['Peak', 'Standard', 'Off-Peak']" :key="'W-' + col">
                  <th v-if="isCompare">{{ col }}<br>Old</th>
                  <th :class="col === 'Off-Peak' ? 'border-right' : ''">{{ col }}<span v-if="isCompare"><br>New</span></th>
                </template>

                <!-- TOU Summer -->
                <template v-for="col in ['Peak', 'Standard', 'Off-Peak']" :key="'S-' + col">
                  <th v-if="isCompare">{{ col }}<br>Old</th>
                  <th :class="col === 'Off-Peak' ? 'border-right' : ''">{{ col }}<span v-if="isCompare"><br>New</span></th>
                </template>

                <!-- Winter Charges -->
                <template v-for="col in ['Basic', 'Demand', 'Access', 'Service', 'Capacity', 'Reactive', 'Legacy', 'Network Demand', 'Generation', 'Electrification', 'Affordability', 'Ancillary', 'Credit Reverse', 'Fixed Monthly', 'Fixed Daily']" :key="'CW-' + col">
                  <th v-if="isCompare">{{ col }}<br>Old</th>
                  <th :class="col === 'Fixed Daily' ? 'border-right' : ''">{{ col }}<span v-if="isCompare"><br>New</span></th>
                </template>

                <!-- Summer Charges -->
                <template v-for="col in ['Basic', 'Demand', 'Access', 'Service', 'Capacity', 'Reactive', 'Legacy', 'Network Demand', 'Generation', 'Electrification', 'Affordability', 'Ancillary', 'Credit Reverse', 'Fixed Monthly', 'Fixed Daily']" :key="'CS-' + col">
                  <th v-if="isCompare">{{ col }}<br>Old</th>
                  <th :class="col === 'Fixed Daily' ? 'border-right' : ''">{{ col }}<span v-if="isCompare"><br>New</span></th>
                </template>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in tariffRows" :key="row.groupKey ?? row.tariffId" class="text-nowrap align-top">
                <td>
                  <input type="checkbox" class="form-check-input"
                    :checked="isSelected(row.groupKey)"
                    @change="toggleSelection(row.groupKey)" />
                </td>
                <td>{{ row.providerName }}</td>
                <td>{{ row.tariffName }}</td>
                <td>{{ row.yearOfTariff }}</td>
                <td>{{ row.source }}</td>
                <td>{{ row.transZone }}</td>
                <td class="border-right">{{ row.voltage }}</td>

                <!-- Block label -->
                <td>
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell">{{ b.label || '—' }}</div>
                  <div v-if="!row.blocks.length">—</div>
                </td>

                <!-- Block Winter rates — aligned to blocks -->
                <td v-if="isCompare">
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell">
                    {{ row.blocksW.find(w => w.label === b.label)?.oldRate ?? '—' }}
                  </div>
                </td>
                <td class="border-right">
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell"
                    :class="bandClass(row.blocksW.find(w => w.label === b.label))"
                    v-tooltip="bandTooltip(row.blocksW.find(w => w.label === b.label), isCompare)">
                    {{ row.blocksW.find(w => w.label === b.label)?.rate ?? '—' }}
                  </div>
                  <div v-if="!row.blocks.length">—</div>
                </td>

                <!-- Block Summer rates — aligned to blocks -->
                <td v-if="isCompare">
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell">
                    {{ row.blocksS.find(s => s.label === b.label)?.oldRate ?? '—' }}
                  </div>
                </td>
                <td class="border-right">
                  <div v-for="b in row.blocks" :key="b.label" class="band-cell"
                    :class="bandClass(row.blocksS.find(s => s.label === b.label))"
                    v-tooltip="bandTooltip(row.blocksS.find(s => s.label === b.label), isCompare)">
                    {{ row.blocksS.find(s => s.label === b.label)?.rate ?? '—' }}
                  </div>
                  <div v-if="!row.blocks.length">—</div>
                </td>

                <!-- TOU Winter -->
                <template v-for="bands in [row.peakW, row.standardW, row.offPeakW]" :key="'tw-' + bands.length">
                  <td v-if="isCompare" class="text-center">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell">{{ b.oldRate }}</div>
                  </td>
                  <td class="text-center" :class="bands === row.offPeakW ? 'border-right' : ''">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell"
                      :class="bandClass(b)" v-tooltip="bandTooltip(b, isCompare)">{{ b.rate }}</div>
                    <div v-if="!bands.length">—</div>
                  </td>
                </template>

                <!-- TOU Summer -->
                <template v-for="bands in [row.peakS, row.standardS, row.offPeakS]" :key="'ts-' + bands.length">
                  <td v-if="isCompare" class="text-center">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell">{{ b.oldRate }}</div>
                  </td>
                  <td class="text-center" :class="bands === row.offPeakS ? 'border-right' : ''">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell"
                      :class="bandClass(b)" v-tooltip="bandTooltip(b, isCompare)">{{ b.rate }}</div>
                    <div v-if="!bands.length">—</div>
                  </td>
                </template>

                <!-- Winter Charges -->
                <template v-for="(bands, label) in {
                  'basicChargeW': row.basicChargeW, 'demandChargeW': row.demandChargeW,
                  'accessChargeW': row.accessChargeW, 'serviceChargeW': row.serviceChargeW,
                  'capacityChargeW': row.capacityChargeW, 'reactiveChargeW': row.reactiveChargeW,
                  'legacyChargeW': row.legacyChargeW, 'networkDemandChargeW': row.networkDemandChargeW,
                  'generationChargeW': row.generationChargeW, 'electrificationChargeW': row.electrificationChargeW,
                  'affordabilityChargeW': row.affordabilityChargeW, 'ancillaryChargeW': row.ancillaryChargeW,
                  'creditReverseChargeW': row.creditReverseChargeW, 'fixedMonthlyChargeW': row.fixedMonthlyChargeW,
                  'fixedDailyChargeW': row.fixedDailyChargeW,
                }" :key="label">
                  <td v-if="isCompare" class="text-center">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell">{{ b.oldRate }}</div>
                  </td>
                  <td class="text-center" :class="label === 'fixedDailyChargeW' ? 'border-right' : ''">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell"
                      :class="bandClass(b)" v-tooltip="bandTooltip(b, isCompare)">{{ b.rate }}</div>
                    <div v-if="!bands.length">—</div>
                  </td>
                </template>

                <!-- Summer Charges -->
                <template v-for="(bands, label) in {
                  'basicChargeS': row.basicChargeS, 'demandChargeS': row.demandChargeS,
                  'accessChargeS': row.accessChargeS, 'serviceChargeS': row.serviceChargeS,
                  'capacityChargeS': row.capacityChargeS, 'reactiveChargeS': row.reactiveChargeS,
                  'legacyChargeS': row.legacyChargeS, 'networkDemandChargeS': row.networkDemandChargeS,
                  'generationChargeS': row.generationChargeS, 'electrificationChargeS': row.electrificationChargeS,
                  'affordabilityChargeS': row.affordabilityChargeS, 'ancillaryChargeS': row.ancillaryChargeS,
                  'creditReverseChargeS': row.creditReverseChargeS, 'fixedMonthlyChargeS': row.fixedMonthlyChargeS,
                  'fixedDailyChargeS': row.fixedDailyChargeS,
                }" :key="label">
                  <td v-if="isCompare" class="text-center">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell">{{ b.oldRate }}</div>
                  </td>
                  <td class="text-center" :class="label === 'fixedDailyChargeS' ? 'border-right' : ''">
                    <div v-for="(b, i) in bands" :key="i" class="band-cell"
                      :class="bandClass(b)" v-tooltip="bandTooltip(b, isCompare)">{{ b.rate }}</div>
                    <div v-if="!bands.length">—</div>
                  </td>
                </template>
              </tr>

              <tr v-if="tariffRows.length === 0">
                <td colspan="40" class="text-center text-muted">
                  We couldn't find any tariffs that match your search.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <DataTablePagination
          v-if="meta && tariffRows.length > 0"
          :total-records="meta?.TotalCount"
          :page="meta?.Page"
          @update:page="page = $event"
          :page-size="meta?.PageSize"
          @update:page-size="pageSize = $event"
        />
        <VerticallyCentered v-else>
          <HeroSplash title="Customize Your View!"
            sub-title="Select options from the dropdowns to populate the screen." :image-url="splashImage" />
        </VerticallyCentered>
      </ContentLoader>

      <div class="mt-4 py-3 px-4 bg-white" style="position: sticky; bottom: 0; z-index: 10;"
        v-if="tariffRows.length > 0">
        <ButtonSecondary class="me-3" :icon="faDownload" @click="downloadCsv" :disabled="!hasSelection">
          Download
        </ButtonSecondary>
        <ButtonHighlighted v-if="isCompare" :icon="faHandPointUp" @click="approveTariffs">
          Approve ({{ selectedRecords.length }})
        </ButtonHighlighted>
      </div>
    </PageContent>

    <ModalTouProfile v-if="touProfiles.isOpen" @close="touProfiles.close()" />
  </PageContent>
</template>

<script lang="ts">
function bandClass(band: { changePercent: number | null } | undefined) {
  if (band?.changePercent == null) return {}
  return {
    'text-red':   band.changePercent > 15,
    'text-green': band.changePercent < 15,
  }
}

function bandTooltip(band: { changePercent: number | null } | undefined, isCompare: boolean) {
  if (!isCompare || band?.changePercent == null) return undefined
  return { placement: 'bottom', text: `Increase: ${band.changePercent}%` }
}
</script>

<style lang="scss" scoped>
.no-bottom-border th { border-bottom: none; }
.border-right        { border-right: 1px solid #D1D1D1; }
.text-red            { color: #FD5624; }
.text-green          { color: #28A09C; }

.band-cell {
  line-height: 1.6;
  padding: 1px 0;

  &:not(:last-child) {
    border-bottom: 1px dashed #e0e0e0;
  }
}
</style>